/**
 * Build the WorkOS AuthKit hosted-login URL.
 *
 * AuthKit's React SDK calls this same `user_management/authorize` endpoint
 * under the hood. We construct it manually so the *Vue* LoginPage (which is
 * outside the React tree and therefore can't call `useAuth().signIn()`) can
 * still trigger the redirect during the migration window.
 *
 * Docs: https://workos.com/docs/reference/user-management/authentication/get-authorization-url
 */
export function buildWorkOSAuthorizeUrl({
  clientId,
  redirectUri,
  provider = 'authkit',
  state,
  screenHint,
  loginHint,
}) {
  if (!clientId || !redirectUri) {
    throw new Error('WorkOS authorize URL requires clientId and redirectUri');
  }
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    provider,
  });
  if (state) params.set('state', state);
  // `screen_hint=sign-up` lands the user on AuthKit's create-account screen
  // instead of the default sign-in screen — required when /register kicks
  // off the WorkOS flow.
  if (screenHint) params.set('screen_hint', screenHint);
  // `login_hint=<email>` pre-fills the email field at AuthKit so an invited
  // user doesn't have to retype the address from their invite email.
  if (loginHint) params.set('login_hint', loginHint);
  return `https://api.workos.com/user_management/authorize?${params.toString()}`;
}

/**
 * Generate a cryptographically-random OAuth ``state`` value and persist it
 * to ``sessionStorage`` so the callback page can validate it later.
 *
 * Mitigates login-CSRF where an attacker tricks a victim into completing an
 * OAuth flow tied to the attacker's account. Stored under
 * ``workos_oauth_state``; one-shot — callback should ``consumeWorkOSState``
 * to read+delete atomically.
 */
export function createAndStoreWorkOSState() {
  const state =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  try {
    sessionStorage.setItem('workos_oauth_state', state);
  } catch {
    // sessionStorage unavailable (private mode etc.) — return state anyway;
    // callback will fail-open with a console warning rather than blocking.
  }
  return state;
}

/**
 * Read and delete the stored OAuth state. Returns ``null`` if none was set
 * (e.g., direct navigation to ``/callback``).
 */
export function consumeWorkOSState() {
  try {
    const v = sessionStorage.getItem('workos_oauth_state');
    sessionStorage.removeItem('workos_oauth_state');
    return v;
  } catch {
    return null;
  }
}

/**
 * Read the build-time provider flag.
 * Mirrors getEnvAuthProvider() in src/utils/authProvider.ts so .js callers
 * (the Vue LoginPage, Vue router) don't need a TS import.
 */
export function getEnvAuthProviderJs() {
  const raw =
    typeof import.meta !== 'undefined' && import.meta.env
      ? import.meta.env.VITE_AUTH_PROVIDER
      : undefined;
  return raw === 'workos' ? 'workos' : 'keycloak';
}
